class DubiaSearch extends HTMLElement {
  static get observedAttributes() {
    return ["show"];
  }

  constructor() {
    super();

    this.holder = this.querySelector(".dr-search-holder");
    this.form = this.querySelector(".dr-search-form");
    this.input = this.querySelector(".dr-search-input");
    this.activeInput = this.input;
    this.externalInputs = [];
    this.externalInputHandlers = [];
    this.clearButton = this.querySelector(".dr-search-clear");
    this.closeButtons = this.querySelectorAll(".dr-close-search-button");
    this.recentList = this.querySelector("[data-recent-searches]");
    this.recentEmpty = this.querySelector("[data-recent-searches-empty]");
    this.searchResults = this.querySelector(".dr-predictive-results");
    this.resultsContent = this.querySelector("[data-search-results-content]");
    this.status = this.querySelector("[data-search-status]");

    this.recentStorageKey = "dubiaSearchRecentQueries";
    this.maxRecentSearches = 6;
    this.abortController = null;
    this.isOpen = false;
    this.openedBy = null;
    this.bodyHadOverflowHidden = false;
    this.lastRequestedQuery = "";
    this.lockedViewportHeight = 0;
    this.lockedViewportWidth = 0;
    this.lockedOrientation = "";

    this.onInputChange = this.debounce(this.onChange.bind(this), 250);
    this.onTriggerClick = this.handleTriggerClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onResultsClick = this.handleResultsClick.bind(this);
    this.onRecentClick = this.handleRecentClick.bind(this);
    this.onViewportChange = this.debounce(this.handleViewportChange.bind(this), 80);

    this.setupEventListeners();
    this.renderRecentSearches();
  }

  connectedCallback() {
    this.setupExternalSearchInputs();
    document.addEventListener("click", this.onTriggerClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onTriggerClick);
    document.removeEventListener("keydown", this.onKeydown);
    if (this.isOpen) {
      this.close({ syncAttribute: false, restoreFocus: false });
    }
    this.externalInputHandlers.forEach((handler) => {
      handler.input.removeEventListener("focus", handler.onFocus);
      handler.input.removeEventListener("input", handler.onInput);

      if (handler.form) {
        handler.form.removeEventListener("submit", handler.onSubmit);
      }
    });
    this.externalInputHandlers = [];
    this.abortActiveRequest();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || name !== "show") {
      return;
    }

    if (newValue === "true") {
      this.open({ syncAttribute: false });
    } else {
      this.close({ syncAttribute: false });
    }
  }

  setupEventListeners() {
    if (!this.holder) {
      return;
    }

    if (this.input) {
      this.input.addEventListener("input", () => {
        this.activeInput = this.input;
        this.syncInputs(this.input.value, this.input);
        this.onInputChange();
      });
      this.input.addEventListener("focus", () => {
        this.activeInput = this.input;
        this.onFocus();
      });
      this.input.addEventListener("focusout", this.onFocusout.bind(this));
    }

    if (this.form) {
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
    }

    if (this.clearButton) {
      this.clearButton.addEventListener("click", this.clearSearch.bind(this));
    }

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", this.close.bind(this));
    });

    if (this.resultsContent) {
      this.resultsContent.addEventListener("click", this.onResultsClick);
    }

    if (this.recentList) {
      this.recentList.addEventListener("click", this.onRecentClick);
    }
  }

  setupExternalSearchInputs() {
    if (this.externalInputHandlers.length) {
      return;
    }

    this.externalInputs = Array.from(document.querySelectorAll("[data-dubia-search-input]"));

    this.externalInputs.forEach((input) => {
      const form = input.closest("form");
      const onFocus = () => {
        if (this.restoringFocus) return;
        this.activeInput = input;
        this.syncInputs(input.value, input);
        this.openedBy = input;
        this.open();
      };
      const onInput = () => {
        this.activeInput = input;
        this.syncInputs(input.value, input);
        this.onInputChange();
      };
      const onSubmit = (event) => {
        const query = this.normalizeQuery(input.value);

        if (!query) {
          event.preventDefault();
          this.openedBy = input;
          this.open();
          return;
        }

        this.addRecentSearch(query);
      };

      input.addEventListener("focus", onFocus);
      input.addEventListener("input", onInput);

      if (form) {
        form.addEventListener("submit", onSubmit);
      }

      this.externalInputHandlers.push({ input, form, onFocus, onInput, onSubmit });
    });
  }

  initFocus() {
    const input = this.getPrimaryInput();

    if (!input) {
      return;
    }

    window.setTimeout(() => {
      if (!this.isOpen) return;
      input.focus();
    }, 40);
  }

  closeOpenMenuDrawer() {
    const openDetails = Array.from(
      document.querySelectorAll("#Details-menu-drawer-container[open], header-drawer details[open]")
    );

    if (!openDetails.length) {
      return;
    }

    const drawers = Array.from(
      new Set(openDetails.map((details) => details.closest("header-drawer")).filter(Boolean))
    );

    drawers.forEach((drawer) => {
      const details = drawer.mainDetailsToggle || drawer.querySelector("details[open]");

      if (
        details &&
        details.hasAttribute("open") &&
        typeof drawer.closeMenuDrawer === "function"
      ) {
        drawer.closeMenuDrawer(new Event("dubia-search-open"), false);
        return;
      }

      drawer.querySelectorAll("details[open]").forEach((detailsElement) => {
        const summary = detailsElement.querySelector("summary");

        detailsElement.classList.remove("menu-opening");
        detailsElement.removeAttribute("open");
        if (summary) {
          summary.setAttribute("aria-expanded", "false");
        }
      });
    });

    document.body.classList.remove(
      "overflow-hidden-mobile",
      "overflow-hidden-tablet",
      "overflow-hidden-desktop"
    );

    if (typeof window.removeTrapFocus === "function") {
      window.removeTrapFocus();
    }

    const footerMenuItem = document.querySelector(".dr-footer-mobile-nav__item--menu");
    if (footerMenuItem) {
      footerMenuItem.setAttribute("aria-expanded", "false");
    }
  }

  open(options = {}) {
    if (options.syncAttribute !== false && this.getAttribute("show") !== "true") {
      this.setAttribute("show", "true");
      return;
    }

    if (this.isOpen || !this.holder) {
      return;
    }

    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const restoreFocusTarget = this.openedBy || activeElement;

    this.closeOpenMenuDrawer();

    this.isOpen = true;
    this.openedBy = restoreFocusTarget;
    this.bodyHadOverflowHidden = document.body.classList.contains("overflow-hidden");
    this.revealHeader();
    this.lockSearchViewport();
    this.updateDrawerOffset();
    this.holder.classList.add("dr-is-open");
    document.documentElement.classList.add("dr-search-open");
    document.body.classList.add("dr-search-open");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onViewportChange);
    window.addEventListener("orientationchange", this.onViewportChange);
    this.renderRecentSearches();
    this.syncInputState();
    this.initFocus();
    window.requestAnimationFrame(() => this.updateDrawerOffset());

    const focusTarget = this.getPrimaryInput();

    if (typeof window.trapFocus === "function" && focusTarget && this.holder.contains(focusTarget)) {
      window.trapFocus(this.holder, focusTarget);
    }
  }

  close(options = {}) {
    if (options.syncAttribute !== false && this.getAttribute("show") !== "false") {
      this.setAttribute("show", "false");
      return;
    }

    if (!this.holder) {
      return;
    }

    this.isOpen = false;
    this.lockedViewportHeight = 0;
    this.lockedViewportWidth = 0;
    this.lockedOrientation = "";
    this.style.removeProperty("--dr-search-shell-height");
    this.holder.classList.remove("dr-is-open", "dr-is-focused", "dr-is-loading");
    document.documentElement.classList.remove("dr-search-open");
    document.body.classList.remove("dr-search-open");
    if (!this.bodyHadOverflowHidden) {
      document.body.classList.remove("overflow-hidden");
    }
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onViewportChange);
    window.removeEventListener("orientationchange", this.onViewportChange);
    this.abortActiveRequest();

    this.getAllInputs().forEach((input) => input.setAttribute("aria-expanded", "false"));
    // Returning keyboard focus to the header input must not reopen the drawer.
    this.restoringFocus = true;
    try {
      if (typeof window.removeTrapFocus === "function") {
        window.removeTrapFocus(options.restoreFocus === false ? null : this.openedBy);
      } else if (
        options.restoreFocus !== false &&
        this.openedBy &&
        typeof this.openedBy.focus === "function"
      ) {
        this.openedBy.focus();
      }
    } finally {
      this.restoringFocus = false;
    }
  }

  revealHeader() {
    const header = document.getElementById("shopify-section-header");

    if (!header || !header.classList.contains("shopify-section-header-hidden")) {
      return;
    }

    header.classList.add("shopify-section-header-sticky", "animate");
    header.classList.remove("shopify-section-header-hidden");
  }

  updateDrawerOffset() {
    const offset = this.getVisibleHeaderOffset();
    const viewportHeight = this.lockedViewportHeight || this.getViewportHeight();
    const shellHeight = Math.max(160, viewportHeight - offset);

    this.style.setProperty("--dr-search-top-offset", `${offset}px`);
    this.style.setProperty("--dr-search-shell-height", `${shellHeight}px`);
  }

  handleViewportChange() {
    if (!this.isOpen) {
      return;
    }

    if (this.hasViewportOrientationChanged()) {
      this.lockSearchViewport();
    }

    this.updateDrawerOffset();
  }

  getVisibleHeaderOffset() {
    const viewportHeight = this.lockedViewportHeight || this.getViewportHeight();
    const maxOffset = Math.max(0, viewportHeight - 160);
    let offset = 0;

    this.getHeaderCandidates().forEach((element) => {
      const styles = window.getComputedStyle(element);

      if (styles.display === "none" || styles.visibility === "hidden") {
        return;
      }

      const rect = element.getBoundingClientRect();
      const hasBox = rect.width > 0 && rect.height > 0;
      const nearViewportTop = rect.top <= 120 && rect.bottom > 0;

      if (!hasBox || !nearViewportTop) {
        return;
      }

      offset = Math.max(offset, Math.ceil(rect.bottom));
    });

    return Math.min(Math.max(offset, 0), maxOffset);
  }

  lockSearchViewport() {
    this.lockedViewportHeight = this.getViewportHeight();
    this.lockedViewportWidth = this.getViewportWidth();
    this.lockedOrientation = this.getViewportOrientation();
  }

  getViewportHeight() {
    return Math.max(
      window.innerHeight || 0,
      document.documentElement.clientHeight || 0
    );
  }

  getViewportWidth() {
    return window.innerWidth || document.documentElement.clientWidth || 0;
  }

  getViewportOrientation() {
    const screenOrientation =
      window.screen && window.screen.orientation && window.screen.orientation.type;

    if (screenOrientation) {
      return screenOrientation;
    }

    return this.getViewportWidth() > this.getViewportHeight() ? "landscape" : "portrait";
  }

  hasViewportOrientationChanged() {
    const currentWidth = this.getViewportWidth();
    const currentOrientation = this.getViewportOrientation();

    return (
      Boolean(this.lockedViewportWidth) &&
      (Math.abs(currentWidth - this.lockedViewportWidth) > 80 ||
        (this.lockedOrientation && currentOrientation !== this.lockedOrientation))
    );
  }

  getHeaderCandidates() {
    const selectors = [
      "#shopify-section-header",
      "#shopify-section-header .header-wrapper",
      "#shopify-section-header header",
      "sticky-header.header-wrapper",
      ".header-wrapper",
    ];
    const candidates = [];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (!element.closest("dubia-search") && !candidates.includes(element)) {
          candidates.push(element);
        }
      });
    });

    return candidates;
  }

  handleTriggerClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target ? target.closest(".dr-open-dubia-search") : null;

    if (!trigger) {
      return;
    }

    if (!trigger.matches("[data-dubia-search-input]")) {
      event.preventDefault();
    }

    this.openedBy = trigger;

    if (trigger.matches("[data-dubia-search-input]")) {
      this.activeInput = trigger;
      this.syncInputs(trigger.value, trigger);
    }

    this.open();
  }

  handleKeydown(event) {
    if (event.key === "Escape" || event.code === "Escape") {
      event.preventDefault();
      this.close();
    }
  }

  handleSubmit(event) {
    const query = this.getQuery();

    if (!query) {
      event.preventDefault();
      this.clearSearch();
      return;
    }

    this.addRecentSearch(query);
  }

  handleResultsClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const link = target ? target.closest(".dr-api-search-item__link, .dr-search-empty-state__link") : null;

    if (!link) {
      return;
    }

    const query = this.getQuery();

    if (query) {
      this.addRecentSearch(query);
    }
  }

  handleRecentClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const removeButton = target ? target.closest("[data-recent-search-remove]") : null;

    if (removeButton) {
      const query = removeButton.getAttribute("data-recent-search-remove");
      this.removeRecentSearch(query);
      return;
    }

    const queryButton = target ? target.closest("[data-recent-search-query]") : null;

    const input = this.getPrimaryInput();

    if (!queryButton || !input) {
      return;
    }

    const query = queryButton.getAttribute("data-recent-search-query") || "";

    if (!query) {
      return;
    }

    this.addRecentSearch(query);
    this.syncInputs(query);
    this.activeInput = input;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus();
  }

  clearSearch() {
    const input = this.getPrimaryInput();

    if (!input) {
      return;
    }

    this.syncInputs("");
    this.activeInput = input;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus();
  }

  getResults(searchTerm) {
    const query = searchTerm.trim();

    if (!query) {
      return;
    }

    this.abortActiveRequest();
    this.abortController = typeof AbortController === "function" ? new AbortController() : null;
    this.lastRequestedQuery = query;
    this.clearResults();
    this.setLoading(true);
    this.setStatus("Searching");

    fetch(this.getSuggestUrl(query), {
      signal: this.abortController ? this.abortController.signal : undefined,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Search request failed");
        }

        return response.json();
      })
      .then((suggestions) => {
        if (this.lastRequestedQuery !== query || this.getQuery() !== query) {
          return;
        }

        const products =
          suggestions &&
          suggestions.resources &&
          suggestions.resources.results &&
          Array.isArray(suggestions.resources.results.products)
            ? suggestions.resources.results.products
            : [];

        this.addRecentSearch(query);
        this.renderResults(products, query);
      })
      .catch((error) => {
        if (error && error.name === "AbortError") {
          return;
        }

        this.addRecentSearch(query);
        this.renderError(query);
      })
      .finally(() => {
        if (this.lastRequestedQuery === query) {
          this.setLoading(false);
        }
      });
  }

  getSuggestUrl(searchTerm) {
    const shopifyRoot =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : this.dataset.rootUrl || "/";
    const root = shopifyRoot.endsWith("/") ? shopifyRoot : `${shopifyRoot}/`;
    const params = new URLSearchParams();

    params.set("q", searchTerm);
    params.set("resources[type]", "product");
    params.set("resources[limit]", "20");
    params.set("section_id", "predictive-search");

    return `${root}search/suggest.json?${params.toString()}`;
  }

  getSearchUrl(searchTerm) {
    const searchUrl = this.dataset.searchUrl || "/search";
    const params = new URLSearchParams();

    params.set("q", searchTerm);
    params.set("options[prefix]", "last");

    return `${searchUrl}?${params.toString()}`;
  }

  getQuery() {
    const input = this.activeInput || this.getPrimaryInput();

    return input ? input.value.trim() : "";
  }

  getAllInputs() {
    return [this.input].concat(this.externalInputs).filter(Boolean);
  }

  getPrimaryInput() {
    const visibleExternalInput = this.getVisibleExternalInput();

    if (visibleExternalInput) {
      return visibleExternalInput;
    }

    return this.input;
  }

  getVisibleExternalInput() {
    return this.externalInputs.find((input) => {
      const rect = input.getBoundingClientRect();
      const styles = window.getComputedStyle(input);

      return rect.width > 0 && rect.height > 0 && styles.display !== "none" && styles.visibility !== "hidden";
    });
  }

  syncInputs(value, sourceInput = null) {
    this.getAllInputs().forEach((input) => {
      if (input !== sourceInput && input.value !== value) {
        input.value = value;
      }
    });
  }

  onChange() {
    const searchTerm = this.getQuery();

    this.syncInputState();

    if (!searchTerm.length) {
      this.abortActiveRequest();
      this.setHasInput(false);
      this.setStatus("");
      this.setLoading(false);
      this.clearResults();
      return;
    }

    this.setHasInput(true);
    this.getResults(searchTerm);
  }

  onFocus() {
    if (this.holder) {
      this.holder.classList.add("dr-is-focused");
    }
  }

  onFocusout() {
    if (this.holder) {
      this.holder.classList.remove("dr-is-focused");
    }
  }

  setHasInput(show = true) {
    if (!this.holder) {
      return;
    }

    this.holder.classList.toggle("dr-has-input", Boolean(show));

    this.getAllInputs().forEach((input) => {
      input.setAttribute("aria-expanded", this.isOpen ? "true" : "false");
    });
  }

  syncInputState() {
    const hasQuery = Boolean(this.getQuery());

    if (this.clearButton) {
      this.clearButton.hidden = !hasQuery;
    }

    this.setHasInput(hasQuery);
  }

  setLoading(isLoading) {
    if (this.holder) {
      this.holder.classList.toggle("dr-is-loading", Boolean(isLoading));
    }
  }

  setStatus(message) {
    if (this.status) {
      this.status.textContent = message;
    }
  }

  clearResults() {
    if (this.resultsContent) {
      this.resultsContent.innerHTML = "";
    }
  }

  renderResults(results, query) {
    if (!this.resultsContent) {
      return;
    }

    if (!results.length) {
      this.renderEmptyResults(query);
      return;
    }

    this.setStatus(`${results.length} result${results.length === 1 ? "" : "s"}`);
    this.resultsContent.innerHTML = `
      <ul class="dr-api-search-results" role="list">
        ${results.map((result) => this.renderProductResult(result)).join("")}
      </ul>
    `;
  }

  renderProductResult(result) {
    const title = this.escapeHTML(result && result.title ? result.title : "View product");
    const url = this.escapeAttribute(result && result.url ? result.url : "#");
    const image = this.getProductImage(result || {});
    const price = this.getProductPrice(result || {});

    return `
      <li class="dr-api-search-item">
        <a class="dr-api-search-item__link" href="${url}">
          <span class="dr-api-search-item__image">
            ${image}
          </span>

          <span class="dr-api-search-item__content">
            <span class="dr-api-search-item__title">${title}</span>
            ${price}
          </span>
        </a>
      </li>
    `;
  }

  renderEmptyResults(query) {
    if (!this.resultsContent) {
      return;
    }

    const safeQuery = this.escapeHTML(query);
    const url = this.escapeAttribute(this.getSearchUrl(query));

    this.setStatus("No results");
    this.resultsContent.innerHTML = `
      <div class="dr-search-empty-state">
        <p>No products found for "${safeQuery}".</p>
        <a class="dr-search-empty-state__link" href="${url}">Search all products</a>
      </div>
    `;
  }

  renderError(query) {
    if (!this.resultsContent) {
      return;
    }

    const safeQuery = this.escapeHTML(query);
    const url = this.escapeAttribute(this.getSearchUrl(query));

    this.setStatus("Search unavailable");
    this.resultsContent.innerHTML = `
      <div class="dr-search-empty-state">
        <p>Search results are unavailable for "${safeQuery}".</p>
        <a class="dr-search-empty-state__link" href="${url}">Search all products</a>
      </div>
    `;
  }

  getProductImage(product) {
    const imageSource =
      this.getNestedValue(product, ["featured_image", "url"]) ||
      this.getNestedValue(product, ["featured_image", "src"]) ||
      this.getNestedValue(product, ["featured_image"]) ||
      this.getNestedValue(product, ["image", "url"]) ||
      this.getNestedValue(product, ["image", "src"]) ||
      this.getNestedValue(product, ["image"]) ||
      this.getNestedValue(product, ["featured_media", "preview_image", "src"]);

    const imageUrl = typeof imageSource === "string" ? imageSource : "";

    if (!imageUrl) {
      return '<span class="dr-search-image-placeholder" aria-hidden="true"></span>';
    }

    const imageAlt =
      this.getNestedValue(product, ["featured_image", "alt"]) ||
      this.getNestedValue(product, ["image", "alt"]) ||
      product.title ||
      "";

    return `<img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(imageAlt)}" loading="lazy" width="84" height="84">`;
  }

  getProductPrice(product) {
    const priceValue = this.firstPresent([
      product.price_min,
      product.price,
      product.min_price,
      product.price_max,
    ]);
    const compareValue = this.firstPresent([
      product.compare_at_price_min,
      product.compare_at_price,
      product.compare_at_price_max,
    ]);
    const price = this.formatPrice(priceValue);

    if (!price) {
      return '<span class="dr-api-search-item__meta">See product details</span>';
    }

    const priceNumber = this.toNumber(priceValue);
    const compareNumber = this.toNumber(compareValue);
    const hasSale = compareNumber > priceNumber && priceNumber > 0;
    const comparePrice = hasSale ? this.formatPrice(compareValue) : "";

    return `
      <span class="dr-api-search-item__price${hasSale ? " dr-api-search-item__price--sale" : ""}">
        <span>${this.escapeHTML(price)}</span>
        ${comparePrice ? `<s>${this.escapeHTML(comparePrice)}</s>` : ""}
      </span>
    `;
  }

  renderRecentSearches() {
    if (!this.recentList || !this.recentEmpty) {
      return;
    }

    const searches = this.getRecentSearches();
    const hasSearches = searches.length > 0;

    this.recentList.hidden = !hasSearches;
    this.recentEmpty.hidden = hasSearches;

    if (!hasSearches) {
      this.recentList.innerHTML = "";
      return;
    }

    this.recentList.innerHTML = searches.map((query) => this.renderRecentSearch(query)).join("");
  }

  renderRecentSearch(query) {
    const safeQuery = this.escapeHTML(query);
    const attrQuery = this.escapeAttribute(query);

    return `
      <li class="dr-recent-searches__item">
        <button type="button" class="dr-recent-searches__query" data-recent-search-query="${attrQuery}">
          <span class="dr-recent-searches__icon" aria-hidden="true">${this.renderClockIcon()}</span>
          <span class="dr-recent-searches__term">${safeQuery}</span>
        </button>
        <button type="button" class="dr-recent-searches__remove" data-recent-search-remove="${attrQuery}" aria-label="Remove ${attrQuery}">
          ${this.renderRemoveIcon()}
        </button>
      </li>
    `;
  }

  addRecentSearch(query) {
    const normalizedQuery = this.normalizeQuery(query);

    if (!normalizedQuery) {
      return;
    }

    const searches = this.getRecentSearches().filter(
      (storedQuery) => storedQuery.toLowerCase() !== normalizedQuery.toLowerCase()
    );

    searches.unshift(normalizedQuery);
    this.setRecentSearches(searches.slice(0, this.maxRecentSearches));
    this.renderRecentSearches();
  }

  removeRecentSearch(query) {
    const normalizedQuery = this.normalizeQuery(query);
    const searches = this.getRecentSearches().filter((storedQuery) => storedQuery !== normalizedQuery);

    this.setRecentSearches(searches);
    this.renderRecentSearches();
  }

  getRecentSearches() {
    try {
      const rawSearches = window.sessionStorage.getItem(this.recentStorageKey);
      const searches = rawSearches ? JSON.parse(rawSearches) : [];

      return Array.isArray(searches)
        ? searches.map((query) => this.normalizeQuery(query)).filter(Boolean)
        : [];
    } catch (error) {
      return [];
    }
  }

  setRecentSearches(searches) {
    try {
      window.sessionStorage.setItem(this.recentStorageKey, JSON.stringify(searches));
    } catch (error) {
      return;
    }
  }

  normalizeQuery(query) {
    return typeof query === "string" ? query.trim().replace(/\s+/g, " ") : "";
  }

  abortActiveRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  getNestedValue(source, path) {
    return path.reduce((value, key) => {
      if (value && Object.prototype.hasOwnProperty.call(value, key)) {
        return value[key];
      }

      return undefined;
    }, source);
  }

  firstPresent(values) {
    return values.find((value) => value !== undefined && value !== null && value !== "");
  }

  formatPrice(value) {
    if (value === undefined || value === null || value === "") {
      return "";
    }

    if (typeof value === "string" && /[$€£¥]|[a-z]/i.test(value)) {
      return value;
    }

    const number = this.toNumber(value);

    if (!Number.isFinite(number)) {
      return "";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: this.getCurrencyCode(),
    }).format(number);
  }

  getCurrencyCode() {
    return window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : "USD";
  }

  toNumber(value) {
    if (value === undefined || value === null || value === "") {
      return 0;
    }

    if (typeof value === "number") {
      return value;
    }

    const number = parseFloat(String(value).replace(/[^0-9.-]/g, ""));

    return Number.isFinite(number) ? number : 0;
  }

  escapeHTML(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  escapeAttribute(value) {
    return this.escapeHTML(value);
  }

  debounce(fn, wait) {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  renderClockIcon() {
    return `
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M7.25 7.25H4.75V4.75" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.12 7.25A8 8 0 1 1 4 11.31" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12 7.75V12L15 14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  renderRemoveIcon() {
    return `
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M6.75 6.75L17.25 17.25M17.25 6.75L6.75 17.25" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    `;
  }
}

if (!customElements.get("dubia-search")) {
  customElements.define("dubia-search", DubiaSearch);
}
